interface WebSocketMessage {
  type?: string;
  data?: any;
  timestamp?: string;
}

interface WebSocketConnection {
  ws: WebSocket | null;
  token: string | null;
  lobbyNumber: string | null;
  bu: string;
  isConnected: boolean;
  isConnecting: boolean;
  heartbeatInterval?: ReturnType<typeof setInterval>;
  connectPromise: Promise<void> | null;
  generation: number;
}

interface CustomerRegisterRequest {
  bu: string;
  lobbyNumber?: string;
}

interface CustomerRegisterResponse {
  token: string;
  lobbyNumber: string;
}

interface EventHandlers {
  onOpen: Array<() => void>;
  onMessage: Array<(data: any) => void>;
  onClose: Array<() => void>;
  onError: Array<(error: any) => void>;
}

const websocketService = {
  connection: {
    ws: null,
    token: null,
    lobbyNumber: null,
    bu: "1",
    isConnected: false,
    isConnecting: false,
    connectPromise: null,
    generation: 0,
  } as WebSocketConnection,

  eventHandlers: {
    onOpen: [],
    onMessage: [],
    onClose: [],
    onError: [],
  } as EventHandlers,

  // Register event handlers
  on(event: string, handler: any): void {
    switch (event) {
      case "open":
        this.eventHandlers.onOpen.push(handler);
        break;
      case "message":
        this.eventHandlers.onMessage.push(handler);
        break;
      case "close":
        this.eventHandlers.onClose.push(handler);
        break;
      case "error":
        this.eventHandlers.onError.push(handler);
        break;
    }
  },

  // Remove event handlers
  off(event: string, handler: any): void {
    let handlers: Array<any> = [];
    switch (event) {
      case "open":
        handlers = this.eventHandlers.onOpen;
        break;
      case "message":
        handlers = this.eventHandlers.onMessage;
        break;
      case "close":
        handlers = this.eventHandlers.onClose;
        break;
      case "error":
        handlers = this.eventHandlers.onError;
        break;
    }

    const index = handlers.indexOf(handler);
    if (index > -1) {
      handlers.splice(index, 1);
    }
  },

  // Connect to WebSocket using transaction code
  async connect(
    transactionCode: string,
    businessUnit: string = "1"
  ): Promise<void> {
    if (this.connection.connectPromise) {
      return this.connection.connectPromise;
    }

    if (this.isConnected()) {
      return;
    }

    this.connection.bu = businessUnit;
    this.connection.isConnecting = true;

    const generation = this.connection.generation + 1;
    this.connection.generation = generation;

    const connectPromise = (async () => {
      try {
        // Register customer and get token
        const requestBody: CustomerRegisterRequest = { bu: businessUnit };
        if (transactionCode) {
          requestBody.lobbyNumber = transactionCode;
        }

        const response = await fetch(
          process.env.REACT_APP_WEBSOCKET_REGISTER_URL!,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        if (!response.ok) {
          throw new Error(`Customer registration failed: ${response.status}`);
        }

        const data: CustomerRegisterResponse = await response.json();

        if (generation !== this.connection.generation) {
          throw new Error("WebSocket connection was cancelled");
        }

        const token = data.token;
        const lobbyNumber = data.lobbyNumber;

        // Connect to customer WebSocket
        const wsUrl = `${process.env.REACT_APP_WEBSOCKET_URL}?token=${token}`;
        const ws = new WebSocket(wsUrl);

        if (generation !== this.connection.generation) {
          ws.close();
          throw new Error("WebSocket connection was cancelled");
        }

        this.connection.token = token;
        this.connection.lobbyNumber = lobbyNumber;
        this.connection.ws?.close();
        this.connection.ws = ws;

        await new Promise<void>((resolve, reject) => {
          let hasOpened = false;
          let isSettled = false;

          const rejectOnce = (error: Error) => {
            if (isSettled) {
              return;
            }

            isSettled = true;
            reject(error);
          };

          const resolveOnce = () => {
            if (isSettled) {
              return;
            }

            isSettled = true;
            resolve();
          };

          ws.onopen = () => {
            if (!this.isActiveSocket(ws, generation)) {
              ws.close();
              rejectOnce(new Error("Stale websocket connection opened"));
              return;
            }

            hasOpened = true;
            this.connection.isConnected = true;
            this.connection.isConnecting = false;

            // ISO27018: do not log lobbyNumber (maps to transactionCode - session identifier)
            console.log(`[WebSocket] Connected (BU: ${businessUnit})`);

            this.startHeartbeat();
            this.eventHandlers.onOpen.forEach((handler) => handler());
            resolveOnce();
          };

          ws.onmessage = (event) => {
            if (!this.isActiveSocket(ws, generation)) {
              return;
            }

            try {
              const data = JSON.parse(event.data);

              if (data.type === "pong") {
                console.log("[WebSocket] Heartbeat pong received");
                return;
              }

              this.eventHandlers.onMessage.forEach((handler) => handler(data));
            } catch (error) {
              console.error("[WebSocket] Failed to parse message:", error);
              this.eventHandlers.onMessage.forEach((handler) =>
                handler(event.data)
              );
            }
          };

          ws.onclose = (event) => {
            if (!this.isActiveSocket(ws, generation)) {
              return;
            }

            this.stopHeartbeat();
            this.connection.ws = null;
            this.connection.isConnected = false;
            this.connection.isConnecting = false;
            this.connection.token = null;
            this.connection.lobbyNumber = null;

            console.log("[WebSocket] Connection closed");
            this.eventHandlers.onClose.forEach((handler) => handler());

            if (!hasOpened) {
              rejectOnce(
                new Error(
                  `WebSocket closed before opening (code: ${event.code || 0})`
                )
              );
            }
          };

          ws.onerror = (error) => {
            if (!this.isActiveSocket(ws, generation)) {
              return;
            }

            this.stopHeartbeat();
            this.connection.isConnected = false;
            this.connection.isConnecting = false;

            console.error("[WebSocket] Error:", error);
            this.eventHandlers.onError.forEach((handler) => handler(error));

            if (!hasOpened) {
              rejectOnce(new Error("WebSocket connection error"));
            }
          };
        });
      } catch (error) {
        if (generation === this.connection.generation) {
          if (this.connection.ws && !this.connection.isConnected) {
            this.connection.ws.close();
            this.connection.ws = null;
          }

          this.connection.isConnected = false;
          this.connection.isConnecting = false;
          this.connection.token = null;
          this.connection.lobbyNumber = null;
        }

        console.error("[WebSocket] Connection error:", error);
        throw error;
      } finally {
        if (generation === this.connection.generation) {
          this.connection.connectPromise = null;
          if (!this.connection.isConnected) {
            this.connection.isConnecting = false;
          }
        }
      }
    })();

    this.connection.connectPromise = connectPromise;
    return connectPromise;
  },

  // Disconnect from WebSocket
  disconnect(): void {
    this.stopHeartbeat(); // Stop heartbeat before disconnecting
    this.connection.generation += 1;
    this.connection.connectPromise = null;
    this.connection.isConnecting = false;

    const activeSocket = this.connection.ws;
    this.connection.ws = null;
    this.connection.isConnected = false;
    this.connection.token = null;
    this.connection.lobbyNumber = null;

    if (activeSocket) {
      activeSocket.onopen = null;
      activeSocket.onmessage = null;
      activeSocket.onclose = null;
      activeSocket.onerror = null;
      activeSocket.close();
    }
  },

  // Send message through WebSocket
  send(message: any): void {
    if (this.connection.ws && this.connection.isConnected) {
      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);
      this.connection.ws.send(messageStr);
    } else {
      console.warn("[WebSocket] Cannot send message - not connected");
    }
  },

  // Get connection status
  isConnected(): boolean {
    return (
      this.connection.isConnected &&
      this.connection.ws?.readyState === WebSocket.OPEN
    );
  },

  // Get connection info
  getConnectionInfo() {
    return {
      isConnected: this.connection.isConnected,
      isConnecting: this.connection.isConnecting,
      lobbyNumber: this.connection.lobbyNumber,
      bu: this.connection.bu,
      token: this.connection.token ? "***" : null, // Hide sensitive token
    };
  },

  // Start heartbeat to keep connection alive
  startHeartbeat(): void {
    if (this.connection.heartbeatInterval) {
      clearInterval(this.connection.heartbeatInterval);
    }

    this.connection.heartbeatInterval = setInterval(() => {
      if (this.isConnected()) {
        // Send ping message to keep connection alive
        this.send({ type: "ping", timestamp: new Date().toISOString() });
        console.log("[WebSocket] Heartbeat ping sent");
      }
    }, 5000); // Every 5 seconds
  },

  // Stop heartbeat
  stopHeartbeat(): void {
    if (this.connection.heartbeatInterval) {
      clearInterval(this.connection.heartbeatInterval);
      this.connection.heartbeatInterval = undefined;
    }
  },

  // Clear all event handlers
  clearEventHandlers(): void {
    this.eventHandlers.onOpen = [];
    this.eventHandlers.onMessage = [];
    this.eventHandlers.onClose = [];
    this.eventHandlers.onError = [];
  },

  isActiveSocket(ws: WebSocket, generation: number): boolean {
    return this.connection.ws === ws && this.connection.generation === generation;
  },

  // Send dashboard notification message
  async sendDashboardNotification(bu: string, message: string): Promise<void> {
    try {
      // Extract domain from WebSocket URL
      const wsUrl =
        process.env.REACT_APP_WEBSOCKET_REGISTER_URL ||
        process.env.REACT_APP_WEBSOCKET_URL;
      if (!wsUrl) {
        console.log("[Dashboard Notification] No WebSocket URL configured");
        return;
      }

      // Convert WebSocket URL to HTTP endpoint
      // Example: wss://domain.com/path -> https://domain.com/send-message-dashboard
      const domain =
        wsUrl
          .replace("wss://", "https://")
          .replace("ws://", "http://")
          .replace("/customer-register", "")
          .replace("/customer-ws", "")
          .split("/")[0] +
        "//" +
        wsUrl.split("//")[1].split("/")[0];

      const dashboardUrl = `${domain}/send-message-dashboard`;

      console.log(
        `[Dashboard Notification] Sending message to ${dashboardUrl}`,
        { bu, message }
      );

      const response = await fetch(dashboardUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bu, message }),
      });

      if (!response.ok) {
        console.log(
          `[Dashboard Notification] Response status: ${response.status}`
        );
      } else {
        console.log("[Dashboard Notification] Message sent successfully");
      }
    } catch (error) {
      // Never block the flow - just log the error
      console.log("[Dashboard Notification] Error sending message:", error);
    }
  },
};

export default websocketService;
export {
  WebSocketMessage,
  WebSocketConnection,
  CustomerRegisterRequest,
  CustomerRegisterResponse,
};
